import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Accordion from "react-bootstrap/Accordion";
import { FaCheck, FaThList, FaTags, FaBoxOpen, FaWarehouse, FaMapMarkerAlt, FaTruckLoading, FaSlidersH, FaGlobeAmericas } from "react-icons/fa";
import { countryOfOrigin } from "../utils/countryOfOrigin";
import { isAuthenticated } from "../../auth";
import { elakayAutoText as text } from "../../pageInside/dataElakay";
import { getElakayLang } from "../../auth";
import RadioBox from "../../pageInside/RadioBox";
import CustomerPriceRange from "../../pageInside/CustomerPriceRange";
import { prices } from "../../pageInside/fixedPrices";
import ElakayFilterShell from "../../../components/SIdeDrawer/ElakayFilterShell";
import { listProviderFacets } from "../services/providerProductService";
import { EMPTY_FILTERS, countActiveFilters } from "./providerFilterState";
import "../../pageInside/CheckboxFilters.css";
import "../../pageInside/RadioFilters.css";

const categoryLabel = (c) => (Array.isArray(c.name) ? [...c.name].reverse().slice(1).join(" › ") || c.name[0] : String(c.name || ""));
const langCode = () => { try { return getElakayLang()?.obj?.code || "en"; } catch { return "en"; } };

const Section = ({ eventKey, icon: Icon, title, children }) => (
  <Accordion.Item eventKey={eventKey} className="SideDrawer1-filter-item">
    <Accordion.Header className="SideDrawer1-filter-header"><Icon className="SideDrawer1-filter-header-icon" /><span className="SideDrawer1-filter-header-text">{title}</span></Accordion.Header>
    <Accordion.Body className="SideDrawer1-filter-body">{children}</Accordion.Body>
  </Accordion.Item>
);

const Check = ({ id, checked, onChange, label, count }) => (
  <li className="SideDrawer1-checkbox-item-modern">
    <input type="checkbox" id={id} className="SideDrawer1-checkbox-input-modern check2" checked={checked} onChange={onChange} />
    <label className="SideDrawer1-checkbox-label-modern" htmlFor={id}>
      <span className="SideDrawer1-checkbox-box"><FaCheck className="SideDrawer1-checkbox-icon" /></span>
      <span className="SideDrawer1-checkbox-text">{label}{count != null ? ` (${count})` : ""}</span>
    </label>
  </li>
);

// Provider contexts of the Filter drawer. Same shell, accordions, checkboxes, radios and price
// slider as the Buyer "Filters" -- only the DATA differs:
//   mode "discovery" -> "Provider Filters": categories, price, buying options, availability, MOQ, location,
//                        then the category's DYNAMIC filters once a category is chosen;
//   mode "shop"      -> "Filters Shop": the same sections limited to ONE Provider's products (no location),
//                        dynamic filters built from that shop's own products.
// Every option and count comes from /seller/providers/facets (real categories / filtersels / products).
export default function ProviderFiltersPanel({ mode = "discovery", filters, onChange, scope = {}, click, gridButon, infoSource, embedded = false }) {
  const shop = mode === "shop";
  const [facets, setFacets] = useState({ categories: [], filters: [], priceRange: null, moqRange: null, locations: [], madeIn: [] });
  const [error, setError] = useState("");
  const [resetKey, setResetKey] = useState(0);
  const key = JSON.stringify({ ...filters, ...scope });
  useEffect(() => {
    let live = true;
    listProviderFacets(isAuthenticated()?.token, { ...filters, ...scope }).then((r) => { if (!live) return; if (r?.error) setError(r.error); else { setError(""); setFacets({ categories: [], filters: [], priceRange: null, moqRange: null, locations: [], madeIn: [], ...r }); } });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  const set = (patch) => onChange({ ...filters, ...patch });
  const code = langCode();
  const range = facets.priceRange;
  const lo = range ? Math.floor(range.min) : 0;
  const hi = range ? Math.max(lo + 1, Math.ceil(range.max)) : 100;
  const applyRange = (r) => {
    if (!r || r.length < 2 || (r[0] <= lo && r[1] >= hi)) return set({ minPrice: "", maxPrice: "" });
    set({ minPrice: String(r[0]), maxPrice: String(r[1]) });
  };
  const toggleAttr = (k, v) => { const id = `${k}::${v}`; set({ attr: filters.attr.includes(id) ? filters.attr.filter((a) => a !== id) : [...filters.attr, id] }); };
  const cats = [...facets.categories].sort((a, b) => categoryLabel(a).localeCompare(categoryLabel(b)));
  const showDynamic = facets.filters.length > 0; // the server only returns them inside a category or a shop
  const clear = () => { setResetKey((n) => n + 1); onChange({ ...EMPTY_FILTERS }); };
  const title = shop ? text("h_pc_filters_shop", "Filters Shop") : text("h_pff_title", "Provider Filters");
  const defaultOpen = ["cat", "buy"];

  return (
    <ElakayFilterShell title={title} embedded={embedded} click={click} gridButon={gridButon} onClear={countActiveFilters(filters) ? clear : undefined}>
      {error && <p role="alert" className="SideDrawer1-filter-error" style={{ color: "#b91c1c", fontSize: 13 }}>{error}</p>}
      <Accordion alwaysOpen defaultActiveKey={defaultOpen} className="SideDrawer1-filters-accordion">
        <Section eventKey="cat" icon={FaThList} title={text("h_pff_category", "Categories")}>
          <div className="SideDrawer1-categories-list">
            {filters.categoryId && (
              <button type="button" className="SideDrawer1-category-link" style={{ background: "none", border: 0 }} onClick={() => set({ categoryId: "", attr: [] })}>
                <div className="SideDrawer1-category-item"><span className="SideDrawer1-category-dot" /><span className="SideDrawer1-category-name">{text("h_pff_all_categories", "All categories")}</span></div>
              </button>
            )}
            {cats.map((c) => {
              const on = String(c._id) === String(filters.categoryId);
              return (
                <button type="button" key={c._id} className="SideDrawer1-category-link" style={{ background: "none", border: 0, textAlign: "left" }} aria-pressed={on} onClick={() => set({ categoryId: on ? "" : String(c._id), attr: [] })}>
                  <div className="SideDrawer1-category-item" style={on ? { borderColor: "var(--elakay-primary)", background: "var(--elakay-primary-soft)" } : undefined}>
                    <span className="SideDrawer1-category-dot" /><span className="SideDrawer1-category-name">{categoryLabel(c)} ({c.products})</span>
                  </div>
                </button>
              );
            })}
            {!cats.length && <span style={{ fontSize: 13, color: "#6d6a7f" }}>{text("h_pff_none", "No categories available")}</span>}
          </div>
        </Section>

        <Section eventKey="price" icon={FaTags} title={text("h_pff_price", "Provider Price")}>
          <div className="SideDrawer1-price-stack" key={`${resetKey}-${lo}-${hi}`}>
            <RadioBox infoSource={infoSource} prices={prices} handleFilters={(id) => applyRange(prices.find((p) => String(p._id) === String(id))?.array)} />
            <CustomerPriceRange label={text("h_pff_price", "Provider Price")} min={lo} max={hi} handleFilters={applyRange} />
          </div>
        </Section>

        <Section eventKey="buy" icon={FaTruckLoading} title={text("h_pff_buying", "Buying Options")}>
          <ul className="SideDrawer1-checkbox-list-modern" style={{ listStyle: "none", padding: 0, margin: 0 }}>
            <Check id="pf-wholesale" checked={filters.wholesale} onChange={(e) => set({ wholesale: e.target.checked })} label={text("h_pff_wholesale", "Wholesale allowed")} />
            <Check id="pf-drop" checked={filters.dropshipping} onChange={(e) => set({ dropshipping: e.target.checked })} label={text("h_pff_dropshipping", "Dropshipping allowed")} />
            <Check id="pf-pa" checked={filters.pa} onChange={(e) => set({ pa: e.target.checked })} label={text("h_pff_pa", "PA discount available")} />
          </ul>
        </Section>

        <Section eventKey="avail" icon={FaBoxOpen} title={text("h_pff_availability", "Availability")}>
          <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
            <Check id="pf-stock" checked={filters.inStock} onChange={(e) => set({ inStock: e.target.checked })} label={text("h_pff_instock", "In stock only")} />
          </ul>
        </Section>

        <Section eventKey="moq" icon={FaWarehouse} title={text("h_pff_moq_title", "Minimum Order Quantity")}>
          <label htmlFor="pf-moq" style={{ fontSize: 13, fontWeight: 600 }}>{text("h_pff_moq", "Maximum minimum order quantity (MOQ)")}</label>
          <input id="pf-moq" type="number" min="1" className="form-control" value={filters.maxMinimumQuantity} placeholder={facets.moqRange ? `${facets.moqRange.min} – ${facets.moqRange.max}` : ""} onChange={(e) => set({ maxMinimumQuantity: e.target.value })} />
        </Section>

        {!shop && facets.locations.length > 0 && (
          <Section eventKey="loc" icon={FaMapMarkerAlt} title={text("h_pff_location", "Location")}>
            <div className="radio-group-modern">
              {[{ value: "", label: text("h_pff_any", "Any location") }, ...facets.locations.map((l) => ({ value: l.value, label: `${l.value} (${l.providers})` }))].map((l) => (
                <div key={l.value || "any"}>
                  <input type="radio" name="pf-country" id={`pf-c-${l.value || "any"}`} className="radio-input-modern check2" checked={filters.country === l.value} onChange={() => set({ country: l.value })} />
                  <label className="radio-label-modern" htmlFor={`pf-c-${l.value || "any"}`}><span className="radio-circle" /><span className="radio-text">{l.label}</span></label>
                </div>
              ))}
            </div>
          </Section>
        )}

        {/* "Made In" (ticket §1) -- where the PRODUCT was manufactured, never the Provider's own
            address/Location above. Only real countries actually present in the catalog (facets.madeIn). */}
        {facets.madeIn.length > 0 && (
          <Section eventKey="madeIn" icon={FaGlobeAmericas} title={text("h_pff_made_in", "Made In")}>
            <div className="radio-group-modern">
              {[{ value: "", label: text("h_pff_any_country", "Any country") }, ...facets.madeIn.map((m) => ({ value: m.value, label: `${countryOfOrigin(m.value)?.name || m.value} (${m.products})` }))].map((m) => (
                <div key={m.value || "any"}>
                  <input type="radio" name="pf-madein" id={`pf-mi-${m.value || "any"}`} className="radio-input-modern check2" checked={filters.madeIn === m.value} onChange={() => set({ madeIn: m.value })} />
                  <label className="radio-label-modern" htmlFor={`pf-mi-${m.value || "any"}`}><span className="radio-circle" /><span className="radio-text">{m.label}</span></label>
                </div>
              ))}
            </div>
          </Section>
        )}

      </Accordion>
      {showDynamic && (
        <>
          <div className="drawer1-section-label drawer1-section-label--sub">{shop ? text("h_pff_shop_filters", "Shop filters") : text("h_pff_category_filters", "Category filters")}</div>
          <Accordion alwaysOpen className="SideDrawer1-filters-accordion">
        {showDynamic && facets.filters.map((f) => (
              <Section key={f.filterId} eventKey={`dyn-${f.filterId}`} icon={FaSlidersH} title={f.translations?.[code] || f.label}>
                <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                  {f.values.map((v) => <Check key={v.value} id={`pf-a-${f.filterId}-${v.value}`} checked={filters.attr.includes(`${f.key}::${v.value}`)} onChange={() => toggleAttr(f.key, v.value)} label={v.value} count={v.products} />)}
                </ul>
              </Section>
            ))}
          </Accordion>
        </>
      )}
      {shop && <Link to="/seller/providers" onClick={click} style={{ fontSize: 13 }}>← {text("h_spm_back_providers", "Back to all providers")}</Link>}
    </ElakayFilterShell>
  );
}
